import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Field } from "./BankAccountsPage";
import { useAppCtx } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

function num(n, d = 2) {
    return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

async function downloadCsv(path, params, defaultName) {
    try {
        const res = await api.get(path, { params, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        // Try to get filename from Content-Disposition; else fallback
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^"]+)"?/);
        a.download = (m ? m[1] : defaultName) || defaultName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
        toast.error(e.response?.data?.detail || "CSV export failed");
    }
}

export default function ReportsPage() {
    const { selectedEntity, selectedSegment } = useAppCtx();
    const { hasReportPerm } = useAuth();
    const [rp, setRp] = useState(null);
    const [pnl, setPnl] = useState(null);
    const [bank, setBank] = useState(null);
    const [nca, setNca] = useState(null);
    const [month, setMonth] = useState("");
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });
    const [includeUnbilled, setIncludeUnbilled] = useState(true);
    const [ohDrill, setOhDrill] = useState(null);  // { ledger, segment } | null
    const [ohDrillData, setOhDrillData] = useState(null);
    const [ohDrillLoading, setOhDrillLoading] = useState(false);

    // Double-click on an Overheads-breakdown row → load the contributing
    // line items (payments, GL receipts/payments, journal lines) and open
    // a drill-down dialog. Same filters apply (entity, segment, month).
    const openOverheadDrill = async (row) => {
        setOhDrill({ ledger: row.ledger, segment: row.segment });
        setOhDrillData(null);
        setOhDrillLoading(true);
        try {
            const params = { ledger: row.ledger, segment: row.segment };
            if (selectedEntity && selectedEntity !== "all") params.entity_id = selectedEntity;
            if (month) params.month = month;
            const { data } = await api.get("/reports/pnl/overhead-ledger-detail", { params });
            setOhDrillData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load ledger detail");
            setOhDrill(null);
        } finally { setOhDrillLoading(false); }
    };

    // Pick the first tab the user is actually allowed to see so granular
    // users don't land on a blank / forbidden tab.
    const firstAllowed = (() => {
        const order = [
            ["receivables_payables", "rp"],
            ["customer_ledger", "ledger"],
            ["pnl", "pnl"],
            ["bank_position", "bank"],
            ["bank_ledger", "bank-ledger"],
            ["bank_reconciliation", "brs"],
            ["nca", "nca"],
            ["pnl_trend", "trend"],
            ["gl_ledger", "gl-ledger"],
            ["audit_log", "audit"],
            ["vendor_reconciliation", "vendor-recon"],
        ];
        for (const [k, v] of order) if (hasReportPerm(k)) return v;
        return "rp";
    })();

    useEffect(() => {
        const params = { ...periodParams(period) };
        if (selectedEntity !== "all") params.entity_id = selectedEntity;
        if (selectedSegment !== "all") params.segment = selectedSegment;
        if (hasReportPerm("receivables_payables")) {
            api.get("/reports/receivables-payables", { params: { ...params, include_unbilled: includeUnbilled } }).then((r) => setRp(r.data)).catch(() => {});
        }
        if (hasReportPerm("pnl")) {
            api.get("/reports/pnl", { params: { entity_id: params.entity_id, segment: params.segment, ...(month ? { month } : {}) } }).then((r) => setPnl(r.data)).catch(() => {});
        }
        if (hasReportPerm("bank_position")) {
            api.get("/reports/bank-position", { params: { ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}) } }).then((r) => setBank(r.data)).catch(() => {});
        }
        if (hasReportPerm("nca")) {
            api.get("/reports/nca", { params: { entity_id: params.entity_id, segment: params.segment, include_unbilled: includeUnbilled } }).then((r) => setNca(r.data)).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntity, selectedSegment, month, period, includeUnbilled]);

    return (
        <div data-testid="reports-page">
            <PageHeader
                title="Reports"
                eyebrow="Reporting"
                description="Real-time reports filterable by entity, segment and period."
            />

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
            </div>

            <Tabs defaultValue={firstAllowed} className="w-full">
                <TabsList className="rounded-sm bg-slate-100 flex flex-wrap">
                    {hasReportPerm("receivables_payables") && <TabsTrigger value="rp" className="rounded-sm" data-testid="report-tab-rp">Receivables / Payables</TabsTrigger>}
                    {hasReportPerm("customer_ledger") && <TabsTrigger value="ledger" className="rounded-sm" data-testid="report-tab-ledger">Customer Ledger</TabsTrigger>}
                    {hasReportPerm("pnl") && <TabsTrigger value="pnl" className="rounded-sm" data-testid="report-tab-pnl">P&amp;L</TabsTrigger>}
                    {hasReportPerm("bank_position") && <TabsTrigger value="bank" className="rounded-sm" data-testid="report-tab-bank">Bank Position</TabsTrigger>}
                    {hasReportPerm("bank_ledger") && <TabsTrigger value="bank-ledger" className="rounded-sm" data-testid="report-tab-bank-ledger">Bank Ledger</TabsTrigger>}
                    {hasReportPerm("bank_reconciliation") && <TabsTrigger value="brs" className="rounded-sm" data-testid="report-tab-brs">Bank Reconciliation</TabsTrigger>}
                    {hasReportPerm("nca") && <TabsTrigger value="nca" className="rounded-sm" data-testid="report-tab-nca">Net Current Assets</TabsTrigger>}
                    {hasReportPerm("pnl_trend") && <TabsTrigger value="trend" className="rounded-sm" data-testid="report-tab-trend">P&amp;L Trend</TabsTrigger>}
                    {hasReportPerm("gl_ledger") && <TabsTrigger value="gl-ledger" className="rounded-sm" data-testid="report-tab-gl-ledger">GL Ledger</TabsTrigger>}
                    {hasReportPerm("audit_log") && <TabsTrigger value="audit" className="rounded-sm" data-testid="report-tab-audit">Audit Log</TabsTrigger>}
                    {hasReportPerm("vendor_reconciliation") && <TabsTrigger value="vendor-recon" className="rounded-sm" data-testid="report-tab-vendor-recon">Vendor Reconciliation</TabsTrigger>}
                </TabsList>

                <TabsContent value="rp" className="mt-3">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <label className="inline-flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer select-none" data-testid="rp-include-unbilled-toggle">
                            <input
                                type="checkbox"
                                checked={includeUnbilled}
                                onChange={(e) => setIncludeUnbilled(e.target.checked)}
                                className="h-3.5 w-3.5 accent-[#002FA7]"
                                data-testid="rp-include-unbilled-checkbox"
                            />
                            <span>Include unbilled usage</span>
                            <span className="text-[10.5px] text-slate-400">{includeUnbilled ? "(billed + unbilled)" : "(billed only)"}</span>
                        </label>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm" variant="outline" className="rounded-sm h-8"
                                data-testid="rp-export-receivables-csv"
                                onClick={() => downloadCsv("/reports/receivables-payables/csv",
                                    { kind: "receivables", include_unbilled: includeUnbilled, ...periodParams(period),
                                      ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}),
                                      ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}) },
                                    "receivables.csv")}
                            ><Download size={13} className="mr-1.5"/>Receivables CSV</Button>
                            <Button
                                size="sm" variant="outline" className="rounded-sm h-8"
                                data-testid="rp-export-payables-csv"
                                onClick={() => downloadCsv("/reports/receivables-payables/csv",
                                    { kind: "payables", include_unbilled: includeUnbilled, ...periodParams(period),
                                      ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}),
                                      ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}) },
                                    "payables.csv")}
                            ><Download size={13} className="mr-1.5"/>Payables CSV</Button>
                        </div>
                    </div>
                    <div className="text-[11.5px] text-slate-500 mb-2 leading-relaxed">
                        {includeUnbilled
                            ? <>Including unbilled — usage uploaded but not yet invoiced is rolled into the balance. Auto-computed from <b>pending sales cycles</b> (receivable) and <b>non-posted purchase cycles</b> (payable). Self-clears on invoice/post.</>
                            : <>Showing <b>billed only</b> — balances reflect what's already in the partner ledger (invoices, payments, opening balances). Toggle on to include unbilled usage.</>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <ReportTable title="Outstanding Receivables" rows={rp?.receivables || []} total={rp?.totals?.receivables_total} positive testid="report-receivables"
                                     unbilledKey={includeUnbilled ? "unbilled_receivable" : null} unbilledLabel="Unbilled" unbilledTotal={rp?.totals?.unbilled_receivable_total}/>
                        <ReportTable title="Outstanding Payables" rows={rp?.payables || []} total={rp?.totals?.payables_total} testid="report-payables"
                                     unbilledKey={includeUnbilled ? "unbilled_payable" : null} unbilledLabel="Unbilled" unbilledTotal={rp?.totals?.unbilled_payable_total}/>
                    </div>
                </TabsContent>

                <TabsContent value="ledger" className="mt-3">
                    <CustomerLedgerTab period={period}/>
                </TabsContent>

                <TabsContent value="pnl" className="mt-3">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[11px] uppercase tracking-widest text-slate-500">Month filter</span>
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm h-8 w-44 font-mono" data-testid="pnl-month-filter"/>
                        {month && <button className="text-[12px] text-slate-500 underline" onClick={() => setMonth("")}>clear</button>}
                        <div className="ml-auto">
                            <Button
                                size="sm" variant="outline" className="rounded-sm h-8"
                                data-testid="pnl-export-csv"
                                onClick={() => downloadCsv("/reports/pnl/csv",
                                    { ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}),
                                      ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}),
                                      ...(month ? { month } : {}) },
                                    "pnl.csv")}
                            ><Download size={13} className="mr-1.5"/>Export CSV</Button>
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 overflow-hidden" data-testid="report-pnl-table">
                        <table className="w-full text-[13px] financial">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                    <th className="px-4 py-2.5">Segment</th>
                                    <th className="px-4 py-2.5 num">Sales</th>
                                    <th className="px-4 py-2.5 num">COGS</th>
                                    <th className="px-4 py-2.5 num">Gross Profit</th>
                                    <th className="px-4 py-2.5 num">Overheads</th>
                                    <th className="px-4 py-2.5 num">Net Profit</th>
                                    <th className="px-4 py-2.5">Base</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(pnl?.breakdown || []).map((b) => (
                                    <tr key={b.segment} className="border-b border-slate-100" data-testid={`report-pnl-${b.segment}`}>
                                        <td className="px-4 py-2"><SegBadge segment={b.segment}/></td>
                                        <td className="px-4 py-2 num">{num(b.sales)}</td>
                                        <td className="px-4 py-2 num">{num(b.cogs)}</td>
                                        <td className={`px-4 py-2 num font-semibold ${b.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{num(b.gross_profit)}</td>
                                        <td className="px-4 py-2 num">{num(b.overheads)}</td>
                                        <td className={`px-4 py-2 num font-semibold ${b.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{num(b.net_profit)}</td>
                                        <td className="px-4 py-2 font-mono text-slate-500">{b.base_currency}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pnl?.overheads_by_ledger?.length > 0 && (
                        <div className="bg-white border border-slate-200 overflow-hidden mt-3" data-testid="report-overhead-breakdown">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] uppercase tracking-widest text-slate-600">
                                Overheads breakdown by ledger
                            </div>
                            <table className="w-full text-[13px] financial">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                                        <th className="px-4 py-2">Segment</th>
                                        <th className="px-4 py-2">Ledger</th>
                                        <th className="px-4 py-2 num">Amount</th>
                                        <th className="px-4 py-2">Base</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pnl.overheads_by_ledger.map((row, i) => (
                                        <tr key={i}
                                            className="border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors"
                                            title="Double-click to drill into this ledger's line items"
                                            onDoubleClick={() => openOverheadDrill(row)}
                                            data-testid={`oh-row-${row.segment}-${row.ledger}`}>
                                            <td className="px-4 py-2"><SegBadge segment={row.segment}/></td>
                                            <td className="px-4 py-2">{row.ledger}</td>
                                            <td className="px-4 py-2 num">{num(row.amount_base)}</td>
                                            <td className="px-4 py-2 font-mono text-slate-500">{row.base_currency}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="px-4 py-2 text-[10.5px] uppercase tracking-widest text-slate-400 border-t border-slate-100">
                                Double-click any row to see contributing transactions
                            </div>
                        </div>
                    )}

                    {/* Overhead-ledger drill-down dialog */}
                    <Dialog open={!!ohDrill} onOpenChange={(o) => { if (!o) setOhDrill(null); }}>
                        <DialogContent className="max-w-5xl" data-testid="oh-drill-dialog">
                            <DialogHeader>
                                <DialogTitle>
                                    {ohDrill?.ledger}
                                    <span className="ml-2 text-[11px] uppercase tracking-widest text-slate-500">
                                        {ohDrill?.segment}{month ? ` · ${month}` : ""}
                                    </span>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="max-h-[60vh] overflow-auto">
                                {ohDrillLoading && <div className="text-[12px] text-slate-500 py-6 text-center">Loading…</div>}
                                {!ohDrillLoading && ohDrillData && (
                                    <>
                                        <table className="w-full text-[12px] financial">
                                            <thead className="sticky top-0 bg-white border-b border-slate-200">
                                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                                                    <th className="px-3 py-2">Date</th>
                                                    <th className="px-3 py-2">Source</th>
                                                    <th className="px-3 py-2">Voucher #</th>
                                                    <th className="px-3 py-2">Party</th>
                                                    <th className="px-3 py-2">Bank</th>
                                                    <th className="px-3 py-2 num">Original</th>
                                                    <th className="px-3 py-2 num">Amount ({ohDrillData.base_currency})</th>
                                                    <th className="px-3 py-2">Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ohDrillData.rows.length === 0 && (
                                                    <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No contributing transactions.</td></tr>
                                                )}
                                                {ohDrillData.rows.map((r, i) => (
                                                    <tr key={`${r.id}-${i}`} className="border-b border-slate-100" data-testid={`oh-drill-row-${i}`}>
                                                        <td className="px-3 py-1.5 font-mono whitespace-nowrap">{r.date || "—"}</td>
                                                        <td className="px-3 py-1.5"><span className="text-[10px] uppercase tracking-widest bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{r.source}</span></td>
                                                        <td className="px-3 py-1.5 font-mono text-[#002FA7]">{r.voucher_number || "—"}</td>
                                                        <td className="px-3 py-1.5">{r.party || "—"}</td>
                                                        <td className="px-3 py-1.5 text-slate-600">{r.bank || "—"}</td>
                                                        <td className="px-3 py-1.5 num font-mono text-slate-600 whitespace-nowrap">
                                                            {num(r.amount_original)} <span className="text-[10px] uppercase opacity-60">{r.currency}</span>
                                                        </td>
                                                        <td className={`px-3 py-1.5 num font-mono whitespace-nowrap ${r.amount < 0 ? "text-emerald-700" : ""}`}>{num(r.amount)}</td>
                                                        <td className="px-3 py-1.5 text-[11.5px] text-slate-600">{r.remarks || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            {ohDrillData.rows.length > 0 && (
                                                <tfoot>
                                                    <tr className="border-t border-slate-200 bg-slate-50">
                                                        <td colSpan={6} className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-slate-600">Total</td>
                                                        <td className="px-3 py-2 num font-mono font-semibold" data-testid="oh-drill-total">{num(ohDrillData.total_amount)} {ohDrillData.base_currency}</td>
                                                        <td className="px-3 py-2 text-[10.5px] text-slate-500">{ohDrillData.row_count} row{ohDrillData.row_count === 1 ? "" : "s"}</td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" className="rounded-sm" onClick={() => setOhDrill(null)} data-testid="oh-drill-close">Close</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>

                <TabsContent value="bank" className="mt-3">
                    <div className="bg-white border border-slate-200 overflow-hidden" data-testid="report-bank-table">
                        <table className="w-full text-[13px] financial">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                    <th className="px-4 py-2.5">Entity</th>
                                    <th className="px-4 py-2.5">Bank</th>
                                    <th className="px-4 py-2.5">Account #</th>
                                    <th className="px-4 py-2.5">Currency</th>
                                    <th className="px-4 py-2.5 num">Original Balance</th>
                                    <th className="px-4 py-2.5 num">Balance (USD)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(bank?.accounts || []).map((a) => (
                                    <tr key={a.bank_id} className="border-b border-slate-100">
                                        <td className="px-4 py-2">{a.entity_name}</td>
                                        <td className="px-4 py-2">{a.bank_name}</td>
                                        <td className="px-4 py-2 font-mono">{a.account_number || "—"}</td>
                                        <td className="px-4 py-2 font-mono">{a.currency}</td>
                                        <td className="px-4 py-2 num">{num(a.balance_original)}</td>
                                        <td className="px-4 py-2 num font-semibold">{num(a.balance_usd)}</td>
                                    </tr>
                                ))}
                                {bank && (
                                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                                        <td className="px-4 py-2 font-semibold" colSpan={5}>Total (USD)</td>
                                        <td className="px-4 py-2 num font-semibold">{num(bank.totals_usd)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="bank-ledger" className="mt-3">
                    <BankLedgerTab period={period} selectedEntity={selectedEntity}/>
                </TabsContent>

                <TabsContent value="brs" className="mt-3">
                    <BankReconciliationTab selectedEntity={selectedEntity}/>
                </TabsContent>

                <TabsContent value="nca" className="mt-3">
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-white border border-slate-200 rounded-sm">
                        <input
                            id="nca-include-unbilled"
                            type="checkbox"
                            className="w-3.5 h-3.5 accent-[#002FA7]"
                            checked={includeUnbilled}
                            onChange={(e) => setIncludeUnbilled(e.target.checked)}
                            data-testid="nca-include-unbilled-toggle"
                        />
                        <label htmlFor="nca-include-unbilled" className="text-[12px] text-slate-700 cursor-pointer select-none">Include unbilled usage</label>
                        <span className="text-[10.5px] text-slate-400">{includeUnbilled ? "(billed + unbilled)" : "(billed only)"}</span>
                    </div>
                    {nca && (
                        <div className="grid md:grid-cols-3 gap-3" data-testid="report-nca">
                            <div className="kpi-card">
                                <div className="kpi-label">Current Assets (USD)</div>
                                <div className="kpi-value text-emerald-700">{num(nca.current_assets)}</div>
                            </div>
                            <div className="kpi-card">
                                <div className="kpi-label">Current Liabilities (USD)</div>
                                <div className="kpi-value text-red-700">{num(nca.current_liabilities)}</div>
                            </div>
                            <div className="kpi-card border-2 border-[#002FA7]">
                                <div className="kpi-label">Net Current Assets</div>
                                <div className={`kpi-value ${nca.nca >= 0 ? "text-emerald-700" : "text-red-700"}`}>{num(nca.nca)}</div>
                            </div>
                            <div className="md:col-span-3 bg-white border border-slate-200 p-5">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Components</div>
                                <table className="w-full text-[13px] financial">
                                    <tbody>
                                        <Row label="Bank position (USD)" value={num(nca.components.bank_usd)}/>
                                        <Row label="Receivables — Voice (USD)" value={num(nca.components.receivables_voice_usd)}/>
                                        <Row label="Receivables — SMS (EUR)" value={num(nca.components.receivables_sms_eur)}/>
                                        <Row label="Payables — Voice (USD)" value={num(nca.components.payables_voice_usd)}/>
                                        <Row label="Payables — SMS (EUR)" value={num(nca.components.payables_sms_eur)}/>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="trend" className="mt-3">
                    <PnLTrendTab />
                </TabsContent>
                <TabsContent value="gl-ledger" className="mt-3">
                    <GLLedgerTab period={period} />
                </TabsContent>
                <TabsContent value="audit" className="mt-3">
                    <AuditLogTab period={period} />
                </TabsContent>
                <TabsContent value="vendor-recon" className="mt-3">
                    <VendorReconTab period={period} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <tr className="border-b border-slate-100">
            <td className="px-2 py-1.5 text-slate-600">{label}</td>
            <td className="px-2 py-1.5 num">{value}</td>
        </tr>
    );
}

function ReportTable({ title, rows, total, positive, testid, unbilledKey, unbilledLabel, unbilledTotal }) {
    const showUnbilled = !!unbilledKey;
    return (
        <div className="bg-white border border-slate-200 overflow-hidden" data-testid={testid}>
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-slate-600">{title}</div>
                <div className="text-right">
                    <div className={`font-mono font-semibold ${positive ? "text-emerald-700" : "text-red-700"}`}>{num(total)}</div>
                    {showUnbilled && Number(unbilledTotal) > 0 && (
                        <div className="text-[10px] text-amber-700 mt-0.5">incl. unbilled {num(unbilledTotal)}</div>
                    )}
                </div>
            </div>
            <table className="w-full text-[13px] financial">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                        <th className="px-4 py-2">Partner</th>
                        <th className="px-4 py-2">Segment</th>
                        {showUnbilled && <th className="px-4 py-2 num" title="Unbilled accruals contributing to this balance">Unbilled</th>}
                        <th className="px-4 py-2 num">Balance</th>
                        <th className="px-4 py-2">Base</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={showUnbilled ? 5 : 4} className="text-center text-slate-500 py-8">Nothing outstanding</td></tr>}
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                            <td className="px-4 py-2">{r.partner_name}</td>
                            <td className="px-4 py-2"><SegBadge segment={r.segment}/></td>
                            {showUnbilled && <td className="px-4 py-2 num text-amber-700">{Number(r[unbilledKey] || 0) > 0 ? num(r[unbilledKey]) : ""}</td>}
                            <td className="px-4 py-2 num font-semibold">{num(r.balance)}</td>
                            <td className="px-4 py-2 font-mono text-slate-500">{r.base_currency}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const METRICS = [
    { key: "sales", label: "Sales", emphasis: "" },
    { key: "cogs", label: "COGS", emphasis: "text-slate-700" },
    { key: "gross_profit", label: "Gross Profit", emphasis: "font-semibold border-t border-slate-200" },
    { key: "overheads", label: "Overheads", emphasis: "text-slate-700" },
    { key: "net_profit", label: "Net Profit", emphasis: "font-semibold border-t border-slate-300" },
];

function defaultRange() {
    const today = new Date();
    const to = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const fromDate = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}`;
    return { from, to };
}

function PnLTrendTab() {
    const { selectedEntity, selectedSegment } = useAppCtx();
    const [range, setRange] = useState(defaultRange());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const params = { from: range.from, to: range.to };
                if (selectedEntity !== "all") params.entity_id = selectedEntity;
                if (selectedSegment !== "all") params.segment = selectedSegment;
                const { data } = await api.get("/reports/pnl-trend", { params });
                if (!cancelled) setData(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [range, selectedEntity, selectedSegment]);

    return (
        <div data-testid="report-pnl-trend">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">From</div>
                        <Input
                            type="month"
                            value={range.from}
                            onChange={(e) => setRange({ ...range, from: e.target.value })}
                            className="rounded-sm h-9 w-44 font-mono"
                            data-testid="trend-from-input"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">To</div>
                        <Input
                            type="month"
                            value={range.to}
                            onChange={(e) => setRange({ ...range, to: e.target.value })}
                            className="rounded-sm h-9 w-44 font-mono"
                            data-testid="trend-to-input"
                        />
                    </div>
                </div>
                {loading && <div className="text-[12px] text-slate-500">Loading…</div>}
                <div className="ml-auto">
                    <Button
                        size="sm" variant="outline" className="rounded-sm h-9"
                        data-testid="trend-export-csv"
                        onClick={() => downloadCsv("/reports/pnl-trend/csv",
                            { from: range.from, to: range.to,
                              ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}),
                              ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}) },
                            "pnl_trend.csv")}
                    ><Download size={13} className="mr-1.5"/>Export CSV</Button>
                </div>
            </div>

            {data?.segments?.map((s) => (
                <SegmentTrendBlock key={s.segment} block={s} months={data.months} />
            ))}

            {data && data.segments?.length === 0 && (
                <div className="text-center text-slate-500 py-10 bg-white border border-slate-200">
                    No data for the selected filters
                </div>
            )}
        </div>
    );
}

function SegmentTrendBlock({ block, months }) {
    const { segment, base_currency, rows, totals } = block;
    const chartData = rows.map((r) => ({
        month: r.month,
        Sales: r.sales,
        "Gross Profit": r.gross_profit,
        "Net Profit": r.net_profit,
    }));

    return (
        <div className="bg-white border border-slate-200 mb-4" data-testid={`trend-${segment}`}>
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <SegBadge segment={segment} />
                    <span className="text-[11px] uppercase tracking-widest text-slate-500">
                        Base currency: <span className="font-mono">{base_currency}</span>
                    </span>
                </div>
                <div className="flex items-center gap-4 text-[12px]">
                    <span className="text-slate-500">Period totals →</span>
                    <span>Sales <span className="font-mono font-semibold">{num(totals.sales)}</span></span>
                    <span className={totals.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}>
                        Net <span className="font-mono font-semibold">{num(totals.net_profit)}</span>
                    </span>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-[12.5px] financial min-w-[800px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <th className="px-3 py-2 sticky left-0 bg-white z-10">Metric</th>
                            {months.map((m) => (
                                <th key={m} className="px-3 py-2 num font-mono">{m}</th>
                            ))}
                            <th className="px-3 py-2 num bg-slate-50">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {METRICS.map((metric) => (
                            <tr key={metric.key} className={`border-b border-slate-100 ${metric.emphasis}`}>
                                <td className="px-3 py-1.5 sticky left-0 bg-white z-10 text-slate-700">{metric.label}</td>
                                {rows.map((r, i) => {
                                    const val = r[metric.key];
                                    const prev = i > 0 ? rows[i - 1][metric.key] : null;
                                    const trend = prev !== null && prev !== 0 ? ((val - prev) / Math.abs(prev)) * 100 : null;
                                    const isProfit = metric.key === "gross_profit" || metric.key === "net_profit";
                                    return (
                                        <td key={r.month} className="px-3 py-1.5 num">
                                            <div className={isProfit ? (val >= 0 ? "text-emerald-700" : "text-red-700") : ""}>
                                                {num(val)}
                                            </div>
                                            {trend !== null && Math.abs(trend) >= 0.1 && (
                                                <div className={`text-[10px] font-mono ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                                    {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                                <td className="px-3 py-1.5 num font-semibold bg-slate-50">{num(totals[metric.key])}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="px-4 py-4 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                    Trend — Sales / Gross / Net
                </div>
                <div className="h-56">
                    <ResponsiveContainer>
                        <LineChart data={chartData} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                            <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="Sales" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="Gross Profit" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="Net Profit" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

function CustomerLedgerTab({ period }) {
    const { entities, selectedEntity, selectedSegment } = useAppCtx();
    const [partners, setPartners] = useState([]);
    const [partnerId, setPartnerId] = useState("");
    const [ledger, setLedger] = useState(null);
    const [busy, setBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailBusy, setEmailBusy] = useState(false);
    const [includeUnbilled, setIncludeUnbilled] = useState(false);

    useEffect(() => {
        api.get("/partners").then((r) => setPartners(r.data || []));
    }, []);

    const params = () => {
        const p = { partner_id: partnerId, include_unbilled: includeUnbilled, ...periodParams(period) };
        if (selectedEntity !== "all") p.entity_id = selectedEntity;
        if (selectedSegment !== "all") p.segment = selectedSegment;
        return p;
    };

    const load = async () => {
        if (!partnerId) { setLedger(null); return; }
        setBusy(true);
        try {
            const { data } = await api.get("/reports/customer-ledger", { params: params() });
            setLedger(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load ledger");
        } finally { setBusy(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [partnerId, period, selectedEntity, selectedSegment, includeUnbilled]);

    const downloadPdf = async () => {
        try {
            const res = await api.get("/reports/customer-ledger/pdf", { params: params(), responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `ledger_${(ledger?.partner?.name || "customer").replace(/\s+/g, "_")}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("PDF download failed");
        }
    };

    const openEmail = () => {
        setEmailTo((ledger?.partner?.emails || []).join(", "));
        setEmailOpen(true);
    };
    const sendEmail = async () => {
        const recipients = (emailTo || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (!recipients.length) return toast.error("At least one recipient is required");
        setEmailBusy(true);
        try {
            const body = {
                partner_id: partnerId,
                from_date: period.from || null,
                to_date: period.to || null,
                to: recipients,
            };
            if (selectedEntity !== "all") body.entity_id = selectedEntity;
            if (selectedSegment !== "all") body.segment = selectedSegment;
            const { data } = await api.post("/reports/customer-ledger/email", body);
            toast.success(`Statement emailed to ${data.recipients.length} recipient(s)`);
            setEmailOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Email failed");
        } finally { setEmailBusy(false); }
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    return (
        <div>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <Field label="Partner">
                    <Select value={partnerId} onValueChange={setPartnerId}>
                        <SelectTrigger className="rounded-sm h-9 w-80" data-testid="ledger-partner-select"><SelectValue placeholder="Select partner"/></SelectTrigger>
                        <SelectContent className="max-h-72">
                            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.country ? ` — ${p.country}` : ""}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
                <div className="flex items-center gap-2 px-3 h-9 bg-white border border-slate-200 rounded-sm">
                    <input
                        id="ledger-include-unbilled"
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-[#002FA7]"
                        checked={includeUnbilled}
                        onChange={(e) => setIncludeUnbilled(e.target.checked)}
                        data-testid="ledger-include-unbilled-toggle"
                    />
                    <label htmlFor="ledger-include-unbilled" className="text-[12px] text-slate-700 cursor-pointer select-none whitespace-nowrap">Include unbilled usage</label>
                    <span className="text-[10.5px] text-slate-400 whitespace-nowrap">{includeUnbilled ? "(billed + unbilled)" : "(billed only)"}</span>
                </div>
                {ledger && (
                    <div className="flex items-center gap-2 ml-auto">
                        <Button variant="outline" size="sm" onClick={() => downloadCsv("/reports/customer-ledger/csv", params(), `ledger_${(ledger?.partner?.name || "customer").replace(/\s+/g, "_")}.csv`)} className="rounded-sm h-9" data-testid="ledger-download-csv-btn"><Download size={13} className="mr-1.5"/>CSV</Button>
                        <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-sm h-9" data-testid="ledger-download-btn"><Download size={13} className="mr-1.5"/>Download PDF</Button>
                        <Button size="sm" onClick={openEmail} className="rounded-sm h-9 bg-[#002FA7]" data-testid="ledger-email-btn"><Mail size={13} className="mr-1.5"/>Email to customer</Button>
                    </div>
                )}
            </div>

            {!partnerId && <div className="text-[12px] text-slate-500 py-16 text-center bg-white border border-slate-200 rounded-sm">Select a partner to view their ledger.</div>}
            {busy && <div className="text-[12px] text-slate-500">Loading…</div>}

            {ledger && (
                <div className="bg-white border border-slate-200 overflow-hidden" data-testid="ledger-table">
                    <div className="px-4 py-3 border-b border-slate-200 text-[12px] text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
                        <div><span className="text-slate-400">Partner:</span> <b>{ledger.partner?.name}</b></div>
                        <div><span className="text-slate-400">Entity:</span> {entityName(ledger.entity?.id)}</div>
                        <div><span className="text-slate-400">Segment:</span> {ledger.segment}</div>
                        <div><span className="text-slate-400">Base:</span> <span className="font-mono">{ledger.base_currency}</span></div>
                    </div>
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                <th className="px-3 py-2.5">Date</th>
                                <th className="px-3 py-2.5">Type</th>
                                <th className="px-3 py-2.5">Reference</th>
                                <th className="px-3 py-2.5 text-right">Debit ({ledger.base_currency})</th>
                                <th className="px-3 py-2.5 text-right">Credit ({ledger.base_currency})</th>
                                <th className="px-3 py-2.5 text-right">Balance</th>
                                <th className="px-3 py-2.5">Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-amber-50 border-b border-slate-100">
                                <td className="px-3 py-1.5" colSpan={2}></td>
                                <td className="px-3 py-1.5 font-semibold">Opening Balance</td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold">{num(ledger.opening_balance_base)}</td>
                                <td></td>
                            </tr>
                            {ledger.rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-slate-500 py-10">No transactions in this period</td></tr>
                            )}
                            {ledger.rows.map((r, i) => (
                                <tr key={i} className={`border-b border-slate-100 ${r.is_unbilled ? "bg-amber-50/60" : ""}`}>
                                    <td className="px-3 py-1.5 font-mono">{r.date}</td>
                                    <td className={`px-3 py-1.5 ${r.is_unbilled ? "text-amber-800 italic" : ""}`}>{r.type}</td>
                                    <td className="px-3 py-1.5 font-mono">{r.reference}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{r.debit_base ? num(r.debit_base) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{r.credit_base ? num(r.credit_base) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{num(r.balance_base)}</td>
                                    <td className="px-3 py-1.5 text-slate-600 truncate max-w-[300px]" title={r.remarks}>{r.remarks}</td>
                                </tr>
                            ))}
                            <tr className="bg-blue-50 border-t border-slate-200">
                                <td className="px-3 py-2" colSpan={2}></td>
                                <td className="px-3 py-2 font-semibold">Closing Balance</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.total_debit_base)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.total_credit_base)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.closing_balance_base)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent className="rounded-sm max-w-lg" data-testid="ledger-email-dialog">
                    <DialogHeader><DialogTitle>Email statement of account</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div className="text-[12px] text-slate-500">The SOA PDF will be attached to a branded email and sent via Mailgun.</div>
                        <Field label="Recipients (comma separated)">
                            <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="rounded-sm h-9 font-mono text-[12px]" data-testid="ledger-email-to"/>
                        </Field>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Button>
                        <Button onClick={sendEmail} disabled={emailBusy} className="rounded-sm bg-[#002FA7]" data-testid="ledger-email-send">{emailBusy ? "Sending…" : "Send email"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function BankLedgerTab({ period, selectedEntity }) {
    const [banks, setBanks] = useState([]);
    const [bankId, setBankId] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
    const [bulkMode, setBulkMode] = useState("book");  // book | cutoff

    useEffect(() => {
        api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
    }, []);

    const filteredBanks = selectedEntity === "all" ? banks : banks.filter((b) => b.entity_id === selectedEntity);

    const load = async () => {
        if (!bankId) { setData(null); return; }
        setLoading(true);
        try {
            const params = { bank_id: bankId, ...periodParams(period) };
            const { data } = await api.get("/reports/bank-ledger", { params });
            setData(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankId, period]);

    const setBankDate = async (row, value) => {
        // Contras live in their own collection and need the leg ("from"|"to")
        // because both legs share the contra id (one is transfer-out from
        // the source bank, the other transfer-in to the destination bank).
        // Receipts and Payments each have their own dedicated endpoint.
        let url;
        let body = { bank_date: value || null };
        if (row.kind === "contra") {
            url = `/contras/${row.id}/bank-date`;
            body = { ...body, side: row.leg };
        } else if (row.kind === "receipt") {
            url = `/receipts/${row.id}/bank-date`;
        } else {
            url = `/payments/${row.id}/bank-date`;
        }
        try {
            await api.put(url, body);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const runBulkClear = async () => {
        try {
            const { data: r } = await api.post("/reports/bank-ledger/mark-cleared-bulk", {
                bank_id: bankId,
                up_to_date: bulkDate,
                use_book_date: bulkMode === "book",
            });
            toast.success(`Cleared ${r.receipts_cleared} receipt(s), ${r.payments_cleared} payment(s)${r.contras_cleared ? `, ${r.contras_cleared} contra leg(s)` : ""}`);
            setBulkOpen(false);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Bulk clear failed"); }
    };

    return (
        <div data-testid="bank-ledger-tab">
            <div className="flex flex-wrap gap-3 mb-3 items-center">
                <Select value={bankId} onValueChange={setBankId}>
                    <SelectTrigger className="w-80 rounded-sm h-9" data-testid="bl-bank-select"><SelectValue placeholder="Select bank account"/></SelectTrigger>
                    <SelectContent>
                        {filteredBanks.length === 0 && <div className="text-[12px] text-slate-500 px-3 py-2">No banks for selected entity</div>}
                        {filteredBanks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} — {b.currency} ({b.account_number || "no a/c #"})</SelectItem>)}
                    </SelectContent>
                </Select>
                {data && (
                    <span className="text-[11px] uppercase tracking-widest text-slate-500">
                        {data.bank.entity_name} · Closing <span className="font-mono font-semibold text-slate-800">{num(data.closing_balance)} {data.bank.currency}</span>
                    </span>
                )}
                {loading && <span className="text-[12px] text-slate-500">Loading…</span>}
                {data && (
                    <div className="ml-auto">
                        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm" variant="outline" className="rounded-sm h-9" data-testid="bl-bulk-clear-btn">
                                    Mark all cleared up to…
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-md" data-testid="bl-bulk-clear-dialog">
                                <DialogHeader><DialogTitle>Bulk-mark cleared</DialogTitle></DialogHeader>
                                <div className="text-[12px] text-slate-500 mb-2">
                                    Sets <span className="font-mono">bank_date</span> on every uncleared transaction with book-date ≤ cutoff for <strong>{data.bank.name}</strong>.
                                </div>
                                <div className="grid gap-3">
                                    <Field label="Cutoff date">
                                        <Input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="bl-bulk-date"/>
                                    </Field>
                                    <Field label="Bank date to apply">
                                        <Select value={bulkMode} onValueChange={setBulkMode}>
                                            <SelectTrigger className="rounded-sm" data-testid="bl-bulk-mode"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="book">Same as book date (assume same-day clearing)</SelectItem>
                                                <SelectItem value="cutoff">Use cutoff date for everything</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
                                    <Button onClick={runBulkClear} className="rounded-sm bg-[#002FA7]" data-testid="bl-bulk-clear-confirm">Mark cleared</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>
            {data && (
                <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="bank-ledger-table">
                    <table className="w-full text-[12.5px] financial min-w-[1100px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                <th className="px-3 py-2.5">Date</th>
                                <th className="px-3 py-2.5">Bank Date</th>
                                <th className="px-3 py-2.5">Type</th>
                                <th className="px-3 py-2.5">Reference</th>
                                <th className="px-3 py-2.5">Party</th>
                                <th className="px-3 py-2.5 num">Dr (in)</th>
                                <th className="px-3 py-2.5 num">Cr (out)</th>
                                <th className="px-3 py-2.5 num">Balance</th>
                                <th className="px-3 py-2.5">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-100 bg-slate-50">
                                <td className="px-3 py-2 font-mono text-slate-500" colSpan={5}><span className="text-[10px] uppercase tracking-widest">Opening Balance</span></td>
                                <td className="px-3 py-2 num"></td>
                                <td className="px-3 py-2 num"></td>
                                <td className="px-3 py-2 num font-semibold">{num(data.opening_balance)}</td>
                                <td className="px-3 py-2"></td>
                            </tr>
                            {data.rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-6">No transactions in this period</td></tr>}
                            {data.rows.map((r) => (
                                <tr key={`${r.kind}-${r.id}-${r.leg || ""}`} className="border-b border-slate-100" data-testid={`bl-row-${r.id}${r.leg ? `-${r.leg}` : ""}`}>
                                    <td className="px-3 py-2 font-mono text-[11.5px]">{r.date || "—"}</td>
                                    <td className="px-3 py-2">
                                        <input type="date" value={r.bank_date || ""}
                                               onChange={(e) => setBankDate(r, e.target.value)}
                                               className="h-7 px-2 border border-slate-200 rounded-sm text-[11.5px] font-mono"
                                               data-testid={`bl-bank-date-${r.id}${r.leg ? `-${r.leg}` : ""}`}/>
                                    </td>
                                    <td className="px-3 py-2">{r.type}</td>
                                    <td className="px-3 py-2 font-mono text-[#002FA7]">{r.reference || "—"}</td>
                                    <td className="px-3 py-2 truncate max-w-[260px]">{r.party}</td>
                                    <td className="px-3 py-2 num text-emerald-700">{r.dr ? num(r.dr) : ""}</td>
                                    <td className="px-3 py-2 num text-rose-700">{r.cr ? num(r.cr) : ""}</td>
                                    <td className="px-3 py-2 num font-semibold">{num(r.balance)}</td>
                                    <td className="px-3 py-2">
                                        {r.bank_date
                                            ? <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">cleared</span>
                                            : <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">uncleared</span>}
                                    </td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td className="px-3 py-2 text-[11px] uppercase tracking-widest text-slate-500" colSpan={5}>Totals / Closing</td>
                                <td className="px-3 py-2 num font-semibold text-emerald-700">{num(data.total_dr)}</td>
                                <td className="px-3 py-2 num font-semibold text-rose-700">{num(data.total_cr)}</td>
                                <td className="px-3 py-2 num font-semibold">{num(data.closing_balance)} {data.bank.currency}</td>
                                <td/>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function BankReconciliationTab({ selectedEntity }) {
    const [banks, setBanks] = useState([]);
    const [bankId, setBankId] = useState("");
    const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => { api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {}); }, []);
    const filteredBanks = selectedEntity === "all" ? banks : banks.filter((b) => b.entity_id === selectedEntity);

    const load = async () => {
        if (!bankId || !asOf) { setData(null); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/reports/bank-reconciliation", { params: { bank_id: bankId, as_of: asOf } });
            setData(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankId, asOf]);

    return (
        <div data-testid="brs-tab">
            <div className="flex flex-wrap gap-3 mb-3 items-center">
                <Select value={bankId} onValueChange={setBankId}>
                    <SelectTrigger className="w-80 rounded-sm h-9" data-testid="brs-bank-select"><SelectValue placeholder="Select bank account"/></SelectTrigger>
                    <SelectContent>
                        {filteredBanks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} — {b.currency}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44 rounded-sm h-9 font-mono" data-testid="brs-as-of"/>
                {loading && <span className="text-[12px] text-slate-500">Loading…</span>}
            </div>
            {data && (
                <div className="space-y-4" data-testid="brs-content">
                    <div className="grid md:grid-cols-3 gap-3">
                        <div className="kpi-card"><div className="kpi-label">Balance per Books</div><div className="kpi-value">{num(data.balance_per_books)} <span className="text-[12px] text-slate-400">{data.bank.currency}</span></div></div>
                        <div className="kpi-card"><div className="kpi-label">Balance per Bank</div><div className="kpi-value">{num(data.balance_per_bank)} <span className="text-[12px] text-slate-400">{data.bank.currency}</span></div></div>
                        <div className="kpi-card"><div className="kpi-label">Reconciled (computed)</div><div className={`kpi-value ${Math.abs(data.difference) < 0.01 ? "text-emerald-700" : "text-red-700"}`}>{num(data.reconciled_balance)}</div><div className="text-[10px] uppercase tracking-widest text-slate-400">Diff: {num(data.difference)}</div></div>
                    </div>

                    <BrsBlock title="Add: Unpresented Payments" subtitle="Recorded in books, not yet cleared in bank" rows={data.unpresented_payments} testid="brs-unpresented" sign="+"/>
                    <BrsBlock title="Less: Deposits in Transit" subtitle="Recorded in books, not yet cleared in bank" rows={data.deposits_in_transit} testid="brs-deposits-in-transit" sign="−"/>
                    <BrsBlock title="Add: Direct Credits" subtitle="Cleared in bank, not yet recorded in books" rows={data.direct_credits} testid="brs-direct-credits" sign="+"/>
                    <BrsBlock title="Less: Direct Debits" subtitle="Cleared in bank, not yet recorded in books" rows={data.direct_debits} testid="brs-direct-debits" sign="−"/>
                </div>
            )}
        </div>
    );
}

function BrsBlock({ title, subtitle, rows, testid, sign }) {
    const total = rows.reduce((s, r) => s + r.amount, 0);
    return (
        <div className="bg-white border border-slate-200" data-testid={testid}>
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center">
                <div>
                    <div className="text-[12.5px] font-semibold">{title}</div>
                    <div className="text-[11px] text-slate-500">{subtitle}</div>
                </div>
                <div className="ml-auto text-[13px] font-mono">{sign} {num(total)}</div>
            </div>
            <table className="w-full text-[12.5px] financial">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <th className="px-3 py-1.5">Book Date</th>
                        <th className="px-3 py-1.5">Bank Date</th>
                        <th className="px-3 py-1.5">Reference</th>
                        <th className="px-3 py-1.5">Party</th>
                        <th className="px-3 py-1.5 num">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-3 text-[11.5px]">None</td></tr>}
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100">
                            <td className="px-3 py-1.5 font-mono text-[11.5px]">{r.date || "—"}</td>
                            <td className="px-3 py-1.5 font-mono text-[11.5px]">{r.bank_date || "—"}</td>
                            <td className="px-3 py-1.5 font-mono text-[#002FA7]">{r.reference || "—"}</td>
                            <td className="px-3 py-1.5 truncate max-w-[260px]">{r.party}</td>
                            <td className="px-3 py-1.5 num font-semibold">{num(r.amount)} <span className="text-[10px] text-slate-400">{r.currency}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function GLLedgerTab({ period }) {
    const [gls, setGls] = useState([]);
    const [glId, setGlId] = useState("");
    const [ledger, setLedger] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/general-ledgers").then((r) => setGls(r.data || []));
    }, []);

    const params = () => ({ gl_id: glId, ...periodParams(period) });

    const load = async () => {
        if (!glId) { setLedger(null); return; }
        setBusy(true);
        try {
            const { data } = await api.get("/reports/gl-ledger", { params: params() });
            setLedger(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load GL ledger");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [glId, period]);

    const gl = ledger?.gl;
    const natureColors = {
        income: "text-emerald-700 bg-emerald-50 border-emerald-200",
        expense: "text-amber-700 bg-amber-50 border-amber-200",
        asset: "text-[#002FA7] bg-blue-50 border-blue-200",
        liability: "text-rose-700 bg-rose-50 border-rose-200",
    };

    return (
        <div>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <Field label="General Ledger account">
                    <Select value={glId} onValueChange={setGlId}>
                        <SelectTrigger className="rounded-sm h-9 w-96" data-testid="gl-ledger-select"><SelectValue placeholder="Select GL account"/></SelectTrigger>
                        <SelectContent className="max-h-72">
                            {gls.length === 0 && <div className="text-[12px] text-slate-500 px-3 py-2">No GL accounts configured</div>}
                            {gls.map((g) => (
                                <SelectItem key={g.id} value={g.id}>
                                    <span className="font-mono text-[10px] text-slate-500 mr-2">{g.code}</span>
                                    {g.name}
                                    <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">{g.nature} · {g.segment}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                {ledger && (
                    <Button variant="outline" size="sm" onClick={() => downloadCsv("/reports/gl-ledger/csv", params(), `gl_${(gl?.name || "ledger").replace(/\s+/g, "_")}.csv`)} className="rounded-sm h-9 ml-auto" data-testid="gl-ledger-download-csv-btn">
                        <Download size={13} className="mr-1.5"/>CSV
                    </Button>
                )}
            </div>

            {!glId && <div className="text-[12px] text-slate-500 py-16 text-center bg-white border border-slate-200 rounded-sm">Select a GL account to view its ledger.</div>}
            {busy && <div className="text-[12px] text-slate-500">Loading…</div>}

            {ledger && gl && (
                <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="gl-ledger-table">
                    <div className="px-4 py-3 border-b border-slate-200 text-[12px] text-slate-600 flex flex-wrap gap-x-6 gap-y-1 items-center">
                        <div><span className="text-slate-400">GL:</span> <b>{gl.name}</b></div>
                        <div className="font-mono text-[11px] text-slate-500">{gl.code}</div>
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${natureColors[gl.nature] || "text-slate-700 bg-slate-50 border-slate-200"}`}>{gl.nature}</span>
                        <div><span className="text-slate-400">Segment:</span> {gl.segment}</div>
                        <div><span className="text-slate-400">Account ccy:</span> <span className="font-mono">{ledger.account_currency}</span></div>
                        <div><span className="text-slate-400">Base ccy:</span> <span className="font-mono">{ledger.base_currency}</span></div>
                    </div>
                    <table className="w-full text-[12.5px] min-w-[1200px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                <th className="px-3 py-2.5">Date</th>
                                <th className="px-3 py-2.5">Type</th>
                                <th className="px-3 py-2.5">Reference</th>
                                <th className="px-3 py-2.5">Counterparty</th>
                                <th className="px-3 py-2.5 text-right">Debit ({ledger.account_currency})</th>
                                <th className="px-3 py-2.5 text-right">Credit ({ledger.account_currency})</th>
                                <th className="px-3 py-2.5 text-right">Balance ({ledger.account_currency})</th>
                                <th className="px-3 py-2.5 text-right border-l border-slate-200">Debit ({ledger.base_currency})</th>
                                <th className="px-3 py-2.5 text-right">Credit ({ledger.base_currency})</th>
                                <th className="px-3 py-2.5 text-right">Balance ({ledger.base_currency})</th>
                                <th className="px-3 py-2.5">Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-amber-50 border-b border-slate-100">
                                <td className="px-3 py-1.5" colSpan={3}></td>
                                <td className="px-3 py-1.5 font-semibold">Opening Balance</td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold">{num(ledger.opening_balance_acct)}</td>
                                <td className="px-3 py-1.5 border-l border-slate-200"></td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold">{num(ledger.opening_balance_base)}</td>
                                <td></td>
                            </tr>
                            {ledger.rows.length === 0 && (
                                <tr><td colSpan={11} className="text-center text-slate-500 py-10">No transactions in this period</td></tr>
                            )}
                            {ledger.rows.map((r, i) => (
                                <tr key={i} className="border-b border-slate-100">
                                    <td className="px-3 py-1.5 font-mono">{r.date}</td>
                                    <td className="px-3 py-1.5">{r.type}</td>
                                    <td className="px-3 py-1.5 font-mono">{r.reference}</td>
                                    <td className="px-3 py-1.5 text-slate-600 truncate max-w-[200px]" title={r.counterparty}>{r.counterparty}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{r.debit_acct ? num(r.debit_acct) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{r.credit_acct ? num(r.credit_acct) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{num(r.balance_acct)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono border-l border-slate-200">{r.debit_base ? num(r.debit_base) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{r.credit_base ? num(r.credit_base) : ""}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{num(r.balance_base)}</td>
                                    <td className="px-3 py-1.5 text-slate-600 truncate max-w-[260px]" title={r.remarks}>{r.remarks}</td>
                                </tr>
                            ))}
                            <tr className="bg-blue-50 border-t border-slate-200">
                                <td className="px-3 py-2" colSpan={3}></td>
                                <td className="px-3 py-2 font-semibold">Closing Balance</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.total_debit_acct)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.total_credit_acct)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.closing_balance_acct)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold border-l border-slate-200">{num(ledger.total_debit_base)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.total_credit_base)}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{num(ledger.closing_balance_base)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}



function AuditLogTab({ period }) {
    const { selectedEntity, selectedSegment, entities } = useAppCtx();
    const [data, setData] = useState(null);
    const [userEmail, setUserEmail] = useState("");
    const [action, setAction] = useState("all");
    const [busy, setBusy] = useState(false);

    const params = () => ({
        ...periodParams(period),
        ...(selectedEntity !== "all" ? { entity_id: selectedEntity } : {}),
        ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}),
        ...(userEmail.trim() ? { user_email: userEmail.trim() } : {}),
        ...(action && action !== "all" ? { action } : {}),
        limit: 1000,
    });

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/reports/audit-log", { params: params() });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load audit log");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedEntity, selectedSegment, period, action, userEmail]);

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    const actionColor = (a) => {
        if (a.endsWith(".posted")) return "text-emerald-700 bg-emerald-50 border-emerald-200";
        if (a.endsWith(".emailed")) return "text-[#002FA7] bg-blue-50 border-blue-200";
        if (a.endsWith(".updated")) return "text-amber-700 bg-amber-50 border-amber-200";
        return "text-slate-700 bg-slate-50 border-slate-200";
    };
    const fmtTs = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso.slice(0, 19);
        return d.toISOString().replace("T", " ").slice(0, 19);
    };

    return (
        <div data-testid="audit-log-tab">
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <Field label="User email contains">
                    <Input
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="e.g. manikandan"
                        className="rounded-sm h-9 w-64 font-mono text-[12px]"
                        data-testid="audit-user-input"
                    />
                </Field>
                <Field label="Action">
                    <Select value={action} onValueChange={setAction}>
                        <SelectTrigger className="rounded-sm h-9 w-56" data-testid="audit-action-select"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All actions</SelectItem>
                            {(data?.actions || []).map((a) => (
                                <SelectItem key={a} value={a}>{a}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] text-slate-500" data-testid="audit-count-label">
                        {data ? `${data.returned} / ${data.total} events` : ""}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadCsv("/reports/audit-log/csv", params(), "audit_log.csv")}
                        className="rounded-sm h-9"
                        data-testid="audit-download-csv-btn"
                        disabled={!data || data.total === 0}
                    >
                        <Download size={13} className="mr-1.5"/>CSV
                    </Button>
                </div>
            </div>

            {busy && !data && <div className="text-[12px] text-slate-500 py-6 text-center">Loading…</div>}

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[12.5px] min-w-[1100px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Timestamp (UTC)</th>
                            <th className="px-3 py-2.5">User</th>
                            <th className="px-3 py-2.5">Action</th>
                            <th className="px-3 py-2.5">Module</th>
                            <th className="px-3 py-2.5">Entity</th>
                            <th className="px-3 py-2.5">Segment</th>
                            <th className="px-3 py-2.5">Reference</th>
                            <th className="px-3 py-2.5 text-right">Amount</th>
                            <th className="px-3 py-2.5 text-right">Base</th>
                            <th className="px-3 py-2.5">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.events.length === 0 && (
                            <tr><td colSpan={10} className="text-center text-slate-500 py-10">No events for the selected filters</td></tr>
                        )}
                        {(data?.events || []).map((e, i) => (
                            <tr key={i} className="border-b border-slate-100" data-testid={`audit-row-${i}`}>
                                <td className="px-3 py-1.5 font-mono text-[11px]">{fmtTs(e.timestamp)}</td>
                                <td className="px-3 py-1.5 font-mono text-[11px]">{e.user_email}</td>
                                <td className="px-3 py-1.5">
                                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${actionColor(e.action)}`}>
                                        {e.action}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5">{e.module}</td>
                                <td className="px-3 py-1.5 text-slate-700 truncate max-w-[180px]" title={entityName(e.entity_id)}>{entityName(e.entity_id)}</td>
                                <td className="px-3 py-1.5">{e.segment ? <SegBadge segment={e.segment}/> : "—"}</td>
                                <td className="px-3 py-1.5 font-mono text-[11px]">{e.doc_ref || "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono">
                                    {e.amount != null ? `${num(e.amount)} ${e.currency || ""}` : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono">
                                    {e.amount_base != null ? `${num(e.amount_base)} ${e.base_currency || ""}` : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-slate-600 truncate max-w-[260px]" title={e.details}>{e.details}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
function VendorReconTab({ period }) {
    const { selectedSegment } = useAppCtx();
    const [cycleType, setCycleType] = useState("all");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const params = () => ({
        ...periodParams(period),
        ...(selectedSegment !== "all" ? { segment: selectedSegment } : {}),
        ...(cycleType !== "all" ? { cycle_type: cycleType } : {}),
    });

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/reports/vendor-reconciliation", { params: params() });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load vendor reconciliation");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, selectedSegment, cycleType]);

    const statusColor = (s) => ({
        pending_recon: "text-slate-600 bg-slate-50 border-slate-200",
        needs_dispute: "text-amber-700 bg-amber-50 border-amber-200",
        reconciled: "text-emerald-700 bg-emerald-50 border-emerald-200",
        disputed: "text-red-700 bg-red-50 border-red-200",
        posted: "text-[#002FA7] bg-blue-50 border-blue-200",
        skipped: "text-slate-500 bg-slate-100 border-slate-300",
    }[s] || "text-slate-600 bg-slate-50 border-slate-200");
    const pctClass = (p) => p === null || p === undefined ? "text-slate-400"
        : Math.abs(p) > 1 ? "text-red-600 font-semibold" : "text-emerald-700";
    const fmtPct = (p) => p === null || p === undefined ? "—" : `${p > 0 ? "+" : ""}${num(p)}%`;
    const fmtVol = (n, seg) => n === null || n === undefined ? "—"
        : seg === "Voice" ? `${num(n)} min` : num(n, 0);

    return (
        <div data-testid="vendor-recon-tab">
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <Field label="Billing cycle">
                    <Select value={cycleType} onValueChange={setCycleType}>
                        <SelectTrigger className="rounded-sm h-9 w-44" data-testid="vendor-recon-cycle-select"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All cycles</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="fortnightly">Fortnightly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <div className="text-[10.5px] text-slate-500 max-w-md leading-tight">
                    Period filter (top of page) defines the date window. Cycles whose start–end overlaps the window are included — e.g. a Weekly cycle 27 Apr → 3 May appears in any range that touches those dates.
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] text-slate-500" data-testid="vendor-recon-count-label">
                        {data ? `${data.count} cycle${data.count === 1 ? "" : "s"}` : ""}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadCsv("/reports/vendor-reconciliation/csv", params(), "vendor_reconciliation.csv")}
                        className="rounded-sm h-9"
                        data-testid="vendor-recon-download-csv-btn"
                        disabled={!data || data.count === 0}
                    >
                        <Download size={13} className="mr-1.5"/>CSV
                    </Button>
                </div>
            </div>

            {data && data.totals_by_segment.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                    {data.totals_by_segment.map((t) => (
                        <div key={t.segment} className="bg-white border border-slate-200 px-4 py-2.5 rounded-sm" data-testid={`vendor-recon-total-${t.segment}`}>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500"><SegBadge segment={t.segment}/> · {t.rows} cycle{t.rows === 1 ? "" : "s"}</div>
                            <div className="flex gap-6 mt-1.5 text-[12px]">
                                <div><span className="text-slate-400">Our:</span> <span className="font-mono font-semibold">{num(t.our_amount)}</span> <span className="text-slate-400">{t.base_currency}</span></div>
                                <div><span className="text-slate-400">Vendor:</span> <span className="font-mono font-semibold">{num(t.vendor_amount)}</span> <span className="text-slate-400">{t.base_currency}</span></div>
                                <div><span className="text-slate-400">Diff:</span> <span className={`font-mono font-semibold ${(t.vendor_amount - t.our_amount) > 0 ? "text-red-600" : "text-emerald-700"}`}>{num(t.vendor_amount - t.our_amount)}</span></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {busy && !data && <div className="text-[12px] text-slate-500 py-6 text-center">Loading…</div>}

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[12.5px] financial min-w-[1180px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-2 py-2.5">Cycle</th>
                            <th className="px-2 py-2.5">Seg</th>
                            <th className="px-2 py-2.5">Partner</th>
                            <th className="px-2 py-2.5 num">Our Vol</th>
                            <th className="px-2 py-2.5 num">Our Amt</th>
                            <th className="px-2 py-2.5 num">Vendor Vol</th>
                            <th className="px-2 py-2.5 num">Vendor Amt</th>
                            <th className="px-2 py-2.5 num">Vol Diff</th>
                            <th className="px-2 py-2.5 num">Amt Diff</th>
                            <th className="px-2 py-2.5">Invoice</th>
                            <th className="px-2 py-2.5">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.count === 0 && (
                            <tr><td colSpan={11} className="text-center text-slate-500 py-10">No cycles for the selected filters</td></tr>
                        )}
                        {(data?.rows || []).map((r) => (
                            <tr key={r.cycle_id} className="border-b border-slate-100" data-testid={`vendor-recon-row-${r.cycle_id}`}>
                                <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                                    <div className="text-[11.5px]">{r.cycle_start} → {r.cycle_end}</div>
                                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">{r.cycle_type}</div>
                                </td>
                                <td className="px-2 py-1.5"><SegBadge segment={r.segment}/></td>
                                <td className="px-2 py-1.5">{r.partner_name}</td>
                                <td className="px-2 py-1.5 num">{fmtVol(r.our_volume, r.segment)}</td>
                                <td className="px-2 py-1.5 num font-semibold">{num(r.our_amount)}</td>
                                <td className="px-2 py-1.5 num">{fmtVol(r.vendor_volume, r.segment)}</td>
                                <td className="px-2 py-1.5 num">{r.vendor_amount === null ? "—" : num(r.vendor_amount)}</td>
                                <td className={`px-2 py-1.5 num ${pctClass(r.volume_diff_pct)}`}>{fmtPct(r.volume_diff_pct)}</td>
                                <td className={`px-2 py-1.5 num ${pctClass(r.amount_diff_pct)}`}>{fmtPct(r.amount_diff_pct)}</td>
                                <td className="px-2 py-1.5">
                                    {r.vendor_invoice_filename
                                        ? <span className="text-[#002FA7] text-[11.5px]">on file</span>
                                        : <span className="text-slate-400 text-[11px]">—</span>}
                                </td>
                                <td className="px-2 py-1.5">
                                    <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${statusColor(r.status)}`}>
                                        {r.status?.replace(/_/g, " ")}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

